<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import CriteriaPanel from './components/CriteriaPanel.vue'
import FiltersBar from './components/FiltersBar.vue'
import KpiGrid from './components/KpiGrid.vue'
import LeadsTable from './components/LeadsTable.vue'
import PipelineCard from './components/PipelineCard.vue'
import RangeBar from './components/RangeBar.vue'
import SummaryTab from './components/SummaryTab.vue'
import { useReport } from './composables/useReport.js'
import { descargarCsv, leadsToCsv } from './lib/csv.js'
import { autoResize, postToParent, readQueryConfig } from './lib/embed.js'
import { duracion, fechaHora, num } from './lib/format.js'
import { rangoPorId } from './lib/ranges.js'

const {
  config, settings, filters, range,
  loading, estimando, error, progress, lastRun, ready, fromCache, rangosEnCache,
  estimacion, avisoVentana, parcial,
  raw, report, visiblePipelines, filteredLeads, owners,
  bootstrap, setRange, confirmarCarga, cancelarCarga, refrescar, resetSettings,
} = useReport()

const PESTANAS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'detalle', label: 'Detalle por proyecto' },
]
const pestana = ref(readQueryConfig().tab === 'detalle' ? 'detalle' : 'resumen')

let detenerResize = () => {}

onMounted(() => {
  const q = readQueryConfig()
  if (q.theme) document.documentElement.dataset.theme = q.theme
  if (config.embed) document.body.classList.add('embed')
  detenerResize = autoResize()
  bootstrap()
})

onBeforeUnmount(() => detenerResize())

const leadFilter = computed(() => {
  const q = filters.search.trim().toLowerCase()
  return (l) => {
    if (filters.reason !== 'todos' && !l.reasons.some((r) => r.id === filters.reason)) return false
    if (filters.severity !== 'todas' && l.severity !== filters.severity) return false
    if (filters.owner === 'sin_asignar' && l.assignedTo) return false
    if (filters.owner !== 'todos' && filters.owner !== 'sin_asignar' && l.assignedTo !== filters.owner) return false
    if (q && !`${l.name} ${l.email || ''} ${l.phone || ''} ${l.stageName}`.toLowerCase().includes(q)) return false
    return true
  }
})

const hayDatos = computed(() => report.value.leads.length > 0)
const vacio = computed(() =>
  !loading.value && !estimando.value && !error.value && !estimacion.value && !hayDatos.value)
const etiquetaRango = computed(() => rangoPorId(range.value).label.toLowerCase())

/** Del resumen al detalle, ya filtrado por el proyecto que se ha pulsado. */
function abrirProyecto(pipelineId) {
  filters.pipelineId = pipelineId
  pestana.value = 'detalle'
  // En iframe el contenedor controla el scroll, asi que se le avisa.
  postToParent({ type: 'ghl-report:scroll-top' })
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function exportar() {
  const dia = new Date().toISOString().slice(0, 10)
  descargarCsv(`leads-no-gestionados-${range.value}-${dia}.csv`, leadsToCsv(filteredLeads.value))
}
</script>

<template>
  <div class="app" :class="{ embed: config.embed }">
    <RangeBar
      :range="range" :rangos-en-cache="rangosEnCache" :loading="loading || estimando"
      :progress="progress" :from-cache="fromCache" :last-run="lastRun"
      :subcuenta="config.demo ? '' : (config.accountName || config.locationId)"
      @range="setRange" @cancelar="cancelarCarga" @refrescar="refrescar"
    />

    <div v-if="error" class="banner no-print">
      <strong>No se pudo generar el informe.</strong>
      <div>{{ error }}</div>
    </div>

    <!-- Rango amplio sin cache: se avisa del coste antes de barrer la subcuenta. -->
    <div v-if="estimacion" class="card aviso no-print">
      <div class="grow">
        <strong>{{ rangoPorId(estimacion.range).label }}: {{ num(estimacion.contactos) }} contactos
          y {{ num(estimacion.oportunidades) }} oportunidades.</strong>
        <p class="muted">
          Son unas {{ num(estimacion.peticiones) }} peticiones al API, alrededor de
          {{ duracion(estimacion.segundos) }} de carga. El informe se va pintando segun
          llegan los datos y puedes cancelar a mitad. Al terminar queda en cache: volver
          a este rango, o a uno mas corto, sera instantaneo.
        </p>
      </div>
      <div class="row">
        <button @click="cancelarCarga">Cancelar</button>
        <button class="primary" @click="confirmarCarga">Cargar de todas formas</button>
      </div>
    </div>

    <!-- La descarga no llego al final: se muestra lo que hay, dicho claramente. -->
    <div v-if="parcial" class="card parcial no-print">
      <strong>Resultado parcial.</strong>
      <span>
        Se descargaron {{ num(parcial.contacts) }}<template v-if="parcial.esperadoContacts">
          de {{ num(parcial.esperadoContacts) }}</template> contactos y
        {{ num(parcial.opportunities) }}<template v-if="parcial.esperadoOpportunities">
          de {{ num(parcial.esperadoOpportunities) }}</template> oportunidades.
        Las cifras de abajo cuentan solo eso, y este rango no se ha guardado en cache.
      </span>
      <button class="primary" @click="refrescar">Reintentar</button>
    </div>

    <template v-if="hayDatos || loading">
      <KpiGrid :totals="report.totals" />

      <p v-if="avisoVentana" class="nota no-print">{{ avisoVentana }}</p>

      <CriteriaPanel :settings="settings" :pipelines="raw.pipelines" @reset="resetSettings" />

      <FiltersBar
        :filters="filters" :pipelines="report.pipelines" :owners="owners"
        :count="filteredLeads.length" @export="exportar"
      />

      <nav class="pestanas no-print" role="tablist">
        <button
          v-for="p in PESTANAS" :key="p.id"
          role="tab" :aria-selected="pestana === p.id"
          :class="{ activa: pestana === p.id }"
          @click="pestana = p.id"
        >{{ p.label }}</button>
      </nav>

      <SummaryTab
        v-if="pestana === 'resumen'"
        :groups="report.pipelines" :totals="report.totals"
        @abrir="abrirProyecto"
      />

      <template v-else>
        <PipelineCard
          v-for="g in visiblePipelines" :key="g.id"
          :group="g" :location-id="config.locationId" :lead-filter="leadFilter"
        />

        <section class="card global">
          <header class="row">
            <h2 class="grow">Listado completo</h2>
            <span class="muted">ordenado por dias sin avanzar</span>
          </header>
          <LeadsTable :leads="filteredLeads" :location-id="config.locationId" show-pipeline />
        </section>
      </template>

      <p class="pie muted">
        Ventana: {{ etiquetaRango }} · generado el {{ fechaHora(lastRun || report.generatedAt) }} ·
        {{ num(report.totals.contacts) }} contactos y {{ num(report.totals.openOpportunities) }}
        oportunidades abiertas analizados · API GoHighLevel v2 (Version {{ config.version }})
      </p>
    </template>

    <div v-else-if="vacio" class="card empty">
      <p v-if="!ready">
        El servidor no tiene subcuenta configurada. Rellena <code>GHL_LOCATION_ID</code>
        y <code>GHL_TOKEN</code> en el <code>.env</code>, o abre el informe con
        <code>?demo=1</code> para ver un ejemplo.
      </p>
      <p v-else>
        Ningun lead cumple los criterios en la ventana de {{ etiquetaRango }}.
        Amplia el rango o baja los umbrales.
      </p>
    </div>
  </div>
</template>

<style scoped>
.aviso { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; padding: 14px 16px; margin-bottom: 16px; }
.aviso p { margin: 3px 0 0; font-size: 12.5px; }
.parcial {
  display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
  padding: 12px 14px; margin-bottom: 16px;
  border-color: color-mix(in srgb, var(--alto) 35%, transparent);
  background: color-mix(in srgb, var(--alto) 9%, transparent);
}
.parcial strong { color: var(--alto); }
.parcial span { flex: 1; min-width: 260px; font-size: 12.5px; color: var(--muted); }
.nota {
  margin: 0 0 16px; padding: 9px 12px; border-radius: 8px; font-size: 12.5px;
  color: var(--alto); background: color-mix(in srgb, var(--alto) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--alto) 28%, transparent);
}

.pestanas { display: flex; gap: 2px; margin-bottom: 14px; border-bottom: 1px solid var(--border); }
.pestanas button {
  background: transparent; border: 0; border-bottom: 2px solid transparent;
  border-radius: 0; padding: 9px 14px; color: var(--muted); font-weight: 600;
}
.pestanas button:hover { color: var(--text); background: transparent; }
.pestanas button.activa { color: var(--accent); border-bottom-color: var(--accent); }

.global { margin-top: 14px; overflow: hidden; }
.global header { padding: 14px 16px; border-bottom: 1px solid var(--border); }
.pie { font-size: 12px; text-align: center; margin: 18px 0 6px; }
code { background: var(--surface-2); padding: 1px 5px; border-radius: 4px; }
</style>
